import { useState } from 'react';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import {
  useGetReviewQueueQuery,
  useUpdateReviewItemMutation,
  type ReviewStatus,
} from '../../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — Review queue (prototype `isQueue`).
 * What clients send back: officers they know, territory corrections,
 * department requests and set-up questions. Approving an item makes it
 * shared data for every client in the state. Live data via the Platform
 * Admin API, scoped to the active state.
 * ------------------------------------------------------------------ */

const STATUS: Record<ReviewStatus, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-[#fffbeb] text-[#b45309]' },
  approved: { label: 'Approved', cls: 'bg-brand-shade text-brand' },
  rejected: { label: 'Rejected', cls: 'bg-muted-bg text-ink-secondary' },
};

type Filter = ReviewStatus | 'all';

export default function ReviewQueuePage() {
  const { code } = usePlatformState();
  const [filter, setFilter] = useState<Filter>('pending');
  const { data: items = [], isLoading } = useGetReviewQueueQuery(
    filter === 'all' ? { code } : { code, status: filter },
  );
  const [updateReviewItem] = useUpdateReviewItemMutation();

  const decide = async (id: number, status: 'approved' | 'rejected') => {
    try {
      await updateReviewItem({ code, id, status }).unwrap();
    } catch {
      /* ignore errors */
    }
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <Breadcrumbs items={[{ label: 'All states', to: '/admin/overview' }, { label: 'Review queue' }]} />
      <section className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-brand">PLATFORM ADMIN</span>
        <h1 className="text-2xl font-semibold text-ink">Review queue</h1>
        <p className="max-w-[780px] text-sm text-ink-secondary">
          What clients send back: officers they know, territory corrections, department requests and
          set-up questions. Approved items become shared data for every client in the state.
        </p>
      </section>

      <div className="flex flex-wrap gap-1.5">
        {([
          ['pending', 'Pending'],
          ['approved', 'Approved'],
          ['rejected', 'Rejected'],
          ['all', 'All'],
        ] as [Filter, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={[
              'h-9 rounded-lg px-3.5 text-[13px] font-semibold transition-colors',
              filter === key ? 'bg-brand text-white' : 'border border-line bg-white text-ink-secondary hover:bg-brand-shade',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading && <div className="text-sm text-ink-secondary">Loading…</div>}

      {!isLoading && items.length === 0 && (
        <div className="rounded-xl border border-line/30 bg-white p-5 text-[13px] text-ink-secondary shadow-card">
          {filter === 'pending' ? 'Nothing to review.' : `No ${filter === 'all' ? '' : filter + ' '}submissions.`}
        </div>
      )}

      {items.map((q) => (
        <section key={q.id} className="flex flex-col gap-1.5 rounded-xl border border-line/30 bg-white p-5 shadow-card">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-brand px-2 py-0.5 text-[11px] font-semibold text-white">{q.type}</span>
            <span className={['rounded px-2 py-0.5 text-[11px] font-semibold', STATUS[q.status].cls].join(' ')}>
              {STATUS[q.status].label}
            </span>
            <span className="text-xs text-ink-secondary">from {q.from_client}</span>
          </div>
          <div className="text-[15px] font-bold text-ink">{q.title}</div>
          <div className="text-sm text-ink">{q.detail}</div>
          <div className="text-xs text-ink-secondary">{q.effect}</div>
          {q.status === 'pending' && (
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={() => decide(q.id, 'approved')}
                className="h-10 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
              >
                Approve
              </button>
              <button
                type="button"
                onClick={() => decide(q.id, 'rejected')}
                className="h-10 rounded-lg border border-line px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-shade"
              >
                Reject
              </button>
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
